import React, { useState } from 'react'
import _ from 'lodash'
import { ListingPreviewBody, Loader, Toast } from '@vzmi/shp_shared_compo'
import { SilentPageProvider } from '../../../../context/silentPageContext'
import { withI13nWrapper } from '../../../Common/I13nWrapper'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util/dist/lib'
import Header from '../../../../components/Common/Header'
import { PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/dist/constants/proposal.js'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import useOnInit from '../../../BatchProposal/hook/useOnInit'
import { EMPTY_VALUE } from '../../../../constants/globalTypes'
import { fetchDefaultAttributes, getProposalById } from '../helpers/previewHelper'
import { retrieveRouteByLocation } from '../../../../../lib/i13nUtil'
import { updateListingProposalI13nConf } from '../../../../../../conf/i13nConf'

const IMAGE_SIZE = 1000

export const PATHS = {
  LISTING: {
    PRICE: 'price',
    FEATURE_TITLE: 'featureTitle',
    DELIVERY_TYPE: 'deliveryType'
  },
  PRODUCT: {
    WARRANTY: 'warranty',
    COPY: 'copy',
    NAME: 'name',
    ATTRIBUTES: 'attributes',
    LISTING_ATTRIBUTES: 'productStructuredDataList.0.attributes',
    SHORT_DESCRIPTION: 'shortDescription',
    SHIP_TYPE: 'skuDetail.shipType.id',
    ATTRIBUTE_DISPLAY_MODE: 'attributeDisplayMode',
    CONTENT_RATING: 'contentRating'
  },
  OTHER: {
    VIDEOS: 'videos',
    VIDEOS_URL: 'videos[0].url',
    MODELS_VIDEOS_URL: 'models[0].videos[0].url',
    MODELS_VIDEOS: 'models[0].videos',
    MODELS_SPEC_NAME: 'models[0].spec.name',
    MODELS_IMAGES: 'models[0].images',
    MODEL: 'models',
    MODEL_ITEMS: 'models[0].items',
    IMAGES: 'images',
    PROPOSAL_ID: 'id',
    PROPOSAL_TYPE: 'proposalType',
    PROPOSAL_LISTING: 'listing',
    LISTING_ID: 'id',
    LISTINGS_PAYLOAD: 'payload.listings.0',
    SKU: 'sku',
    PRODUCTS_PAYLOAD: 'payload.products',
    STRU_DATA_ATTR_CLUSTER_ID: 'struDataAttrClusterId',
    CONVERTED_ATTRIBUTES: '[0].convertedAttributes'
  }
}

const defaultProposal = {
  listing: {
    featureTitle: EMPTY_VALUE,
    price: EMPTY_VALUE,
    deliveryType: EMPTY_VALUE
  },
  product: {
    attributeDisplayMode: EMPTY_VALUE,
    contentRating: EMPTY_VALUE,
    gameContents: [],
    copy: EMPTY_VALUE,
    name: EMPTY_VALUE,
    shortDescription: [],
    warranty: {
      description: EMPTY_VALUE,
      handler: EMPTY_VALUE,
      period: EMPTY_VALUE,
      scope: EMPTY_VALUE
    },
    attributes: [],
    shipType: EMPTY_VALUE,
    specs: [],
    images: [],
    videos: []
  }
}

export const extractVideo = ({ listingData, proposalData }) => {
  const [proposalVideos, proposalVideoUrl, proposalModelsVideos, proposalModelsVideoUrl] = _.map(
    [PATHS.OTHER.VIDEOS, PATHS.OTHER.VIDEOS_URL, PATHS.OTHER.MODELS_VIDEOS, PATHS.OTHER.MODELS_VIDEOS_URL],
    field => _.get(proposalData, field, false)
  )
  // 只有 proposalData 會有刪除 video 的情況，有包含 videos 但沒有 url
  const isVideoDeleted = !!proposalVideos && !proposalVideoUrl
  const isModelVideoDeleted = !!proposalModelsVideos && !proposalModelsVideoUrl
  if (isVideoDeleted || isModelVideoDeleted) {
    // 回傳 undefined 標示已刪除影片
    return
  }

  const videoUrl = proposalVideoUrl ||
    proposalModelsVideoUrl ||
    _.get(listingData, PATHS.OTHER.VIDEOS_URL, false) ||
    _.get(listingData, PATHS.OTHER.MODELS_VIDEOS_URL, false) ||
    defaultProposal.product.videos

  return Array.isArray(videoUrl) ? videoUrl : [videoUrl]
}
export const extractSpecs = ({ listingData, proposalData }) => {
  const isProposalModelsNameExists = !!_.get(proposalData, PATHS.OTHER.MODELS_SPEC_NAME, false)
  const isListingModelsExist = !!_.get(listingData, PATHS.OTHER.MODELS_SPEC_NAME, false)
  const extractSpecsFromModels = (models) => {
    const specsSet = models.reduce((acc, model) => {
      acc[model.spec.name] = acc[model.spec.name] || new Set()
      acc[model.spec.name].add(model.spec.selectedValue)

      if (model.items) {
        model.items.forEach(item => {
          acc[item.spec.name] = acc[item.spec.name] || new Set()
          acc[item.spec.name].add(item.spec.selectedValue)
        })
      }

      return acc
    }, {})

    const specs = Object.keys(specsSet).map(key => ({
      name: key,
      options: Array.from(specsSet[key])
    }))

    return specs
  }

  if (isProposalModelsNameExists) {
    return extractSpecsFromModels(proposalData.models)
  } else if (isListingModelsExist) {
    return extractSpecsFromModels(listingData.models)
  }

  return defaultProposal.product.specs
}

export const filterImagesBySize = (images) => {
  return _.filter(images, image => image.height === IMAGE_SIZE && image.width === IMAGE_SIZE).map(image => image.url)
}

export const extractUpdateListingModelImageUrls = ({ proposalData, originModels }) => {
  const isModelsImagesExist = !!_.get(proposalData, PATHS.OTHER.MODEL, false)

  // 無屬性的情況直接回傳空陣列
  if (!isModelsImagesExist) {
    return []
  }

  const findOriginModelItemImages = (itemSku, originModels) => {
    let foundImages = null

    for (const model of originModels) {
      const item = model.items.find(item => item.sku === itemSku)
      if (item) {
        foundImages = model.images // 獲取該 model 的 images
        break
      }
    }
    return foundImages
  }

  const findTwoLayerItemsImage = (model) => {
    let itemImages = []

    for (const item of model.items) {
      const itemSku = _.get(item, PATHS.OTHER.SKU, false)
      if (itemSku) {
        itemImages = findOriginModelItemImages(itemSku, originModels)
        break
      } else {
        itemImages = item.images
      }
    }
    return _.flatten(itemImages)
  }
  // 雙層會有items
  const isTwoLayer = !!_.get(proposalData, PATHS.OTHER.MODEL_ITEMS, false)

  // 新增商品的圖片在 proposalData 不是新增商品，要從 originModels 找到對應的圖片
  return proposalData.models.map(model => {
    const modelSku = _.get(model, PATHS.OTHER.SKU, false)
    // 有sku表示不是新增商品，要從originModels找到對應的圖片

    const imagesToFilter = modelSku
      ? _.get(_.find(originModels, { sku: modelSku }), PATHS.OTHER.IMAGES, [])
      : isTwoLayer
        // 雙層從items裡面可以找到images
        ? findTwoLayerItemsImage(model)
        : model.images

    return filterImagesBySize(imagesToFilter)
  })
}

export const extractGroupedImageUrls = ({ listingData, proposalData }) => {
  const isProposalModelsImagesExist = !!_.get(proposalData, PATHS.OTHER.MODELS_IMAGES, false)
  const isPropssalImagesExist = !!_.get(proposalData, PATHS.OTHER.IMAGES, false)

  const extractImagesFromModel = (models) => {
    return models.map(model => filterImagesBySize(model.images))
  }
  // 以 proposalData 優先取沒有再從 listingData 取
  const source = isProposalModelsImagesExist || isPropssalImagesExist
    ? proposalData
    : listingData

  if (source.models) {
    return extractImagesFromModel(source.models)
  } else if (source.images) {
    return [filterImagesBySize(source.images)]
  }

  return defaultProposal.product.images
}

// UPDATE_ATTRIBUTE 以外皆可以從 proposal 取得 specs
export const extractAttribute = ({ proposalData, listingData, defaultAttributesNames, specs = extractSpecs({ proposalData, listingData }) }) => {
  const processAttributes = (dataAttrs, dataDisplayMode, specAttrs) => {
    const customAttrs = _.filter(dataAttrs, attribute =>
      !_.some(specAttrs, { name: attribute.name }) && !_.includes(defaultAttributesNames, attribute.name)
    )
    const filteredDataAttrs = _.differenceBy(dataAttrs, [...specAttrs, ...customAttrs], 'name')
    const [attrs, additionalSpecAttrs] = _.partition([...specAttrs, ...filteredDataAttrs], attribute => {
      return _.includes(defaultAttributesNames, attribute.name)
    })

    return {
      attributes: [...attrs, ...additionalSpecAttrs, ...customAttrs],
      attributeDisplayMode: dataDisplayMode
    }
  }

  const specAttrs = _.size(specs) > 0
    ? _.map(specs, spec => {
      return {
        name: spec.name,
        values: spec.options
      }
    })
    : []

  if (proposalData) {
    const proposalAttrs = _.get(proposalData, PATHS.PRODUCT.ATTRIBUTES, false)
    const proposalAttrsDisplayMode = _.get(proposalData, PATHS.PRODUCT.ATTRIBUTE_DISPLAY_MODE, false)
    // UPDATE_LISTING 勾選不修改時，會發生有 proposalData 但找不到的情況
    if (!proposalAttrs) {
      return {
        attributes: proposalAttrs,
        attributeDisplayMode: proposalAttrsDisplayMode
      }
    }

    return processAttributes(proposalAttrs, proposalAttrsDisplayMode, specAttrs)
  }
  if (listingData) {
    const listingAttrs = _.get(listingData, PATHS.PRODUCT.LISTING_ATTRIBUTES, defaultProposal.product.attributes)
    const listingAttrsDisplayMode = _.get(listingData, PATHS.PRODUCT.ATTRIBUTE_DISPLAY_MODE, defaultProposal.product.attributeDisplayMode)

    return processAttributes(listingAttrs, listingAttrsDisplayMode, specAttrs)
  }
}

export const extractCopyAndWarranty = ({ proposalData, listingData }) => {
  // UPDATE_LISTING 勾選不修改時，會發生有 proposalData 但找不到的情況
  if (proposalData) {
    return {
      copy: _.get(proposalData, PATHS.PRODUCT.COPY, false),
      warranty: _.get(proposalData, PATHS.PRODUCT.WARRANTY, false)
    }
  }

  return {
    copy: _.get(listingData, PATHS.PRODUCT.COPY, defaultProposal.product.copy),
    warranty: _.get(listingData, PATHS.PRODUCT.WARRANTY, defaultProposal.product.warranty)
  }
}

export const constructSanitizedProposal = ({ listingData, productsData, defaultAttributesNames }) => {
  const gameContents = _.uniq(_.flatMap(productsData, product => Array.isArray(product.gameContents) ? product.gameContents : defaultProposal.product.gameContents))
  const specs = extractSpecs({ listingData })
  const images = extractGroupedImageUrls({ listingData })
  const video = extractVideo({ listingData })
  const { copy, warranty } = extractCopyAndWarranty({ listingData })
  const { attributeDisplayMode, attributes } = extractAttribute({ listingData, defaultAttributesNames })
  const sanitizedProposal = {
    listing: {
      featureTitle: _.get(listingData, PATHS.LISTING.FEATURE_TITLE, defaultProposal.listing.featureTitle),
      price: _.get(listingData, PATHS.LISTING.PRICE, defaultProposal.listing.price),
      deliveryType: _.get(listingData, PATHS.LISTING.DELIVERY_TYPE, defaultProposal.listing.deliveryType)
    },
    product: {
      name: _.get(listingData, PATHS.PRODUCT.NAME, defaultProposal.product.name),
      contentRating: _.get(listingData, PATHS.PRODUCT.CONTENT_RATING, defaultProposal.product.contentRating),
      shortDescription: _.get(listingData, PATHS.PRODUCT.SHORT_DESCRIPTION, defaultProposal.product.shortDescription),
      shipType: _.get(listingData, PATHS.PRODUCT.SHIP_TYPE, defaultProposal.product.shipType),
      gameContents,
      ...(copy ? { copy } : {}),
      ...(warranty
        ? {
          warranty: {
            ...warranty,
            scope: warranty.custom_scope || warranty.scope
          }
        }
        : {}),
      attributeDisplayMode,
      attributes,
      specs,
      images,
      videos: video || defaultProposal.product.videos
    }
  }

  return sanitizedProposal
}

export const updateProposalDataByType = ({ proposalData, listingProposal, defaultAttributesNames, queryParams, originModels }) => {
  let newProposal = _.cloneDeep(listingProposal)
  const proposalType = _.get(queryParams, PATHS.OTHER.PROPOSAL_TYPE, EMPTY_VALUE)

  switch (proposalType) {
    case PROPOSAL_TYPE.UPDATE_ATTRIBUTE: {
      // UPDATE_ATTRIBUTE 無法從從 proposal 取得 specs ， 所以傳入 listingProposal 的 specs
      const { attributeDisplayMode, attributes } = extractAttribute({ proposalData, defaultAttributesNames, specs: newProposal.product.specs })
      newProposal.product = {
        ...newProposal.product,
        ...(attributeDisplayMode ? { attributeDisplayMode } : {}),
        ...(attributes ? { attributes } : {})
      }
      break
    }
    case PROPOSAL_TYPE.UPDATE_COPY: {
      const { copy, warranty } = extractCopyAndWarranty({ proposalData })

      newProposal.product = {
        ...newProposal.product,
        ...(copy ? { copy } : {}),
        ...(warranty
          ? {
            warranty: {
              ...warranty,
              scope: warranty.custom_scope || warranty.scope
            }
          }
          : {})
      }
      break
    }
    case PROPOSAL_TYPE.UPDATE_IMAGE: {
      const images = extractGroupedImageUrls({ proposalData })
      newProposal.product.images = images
      break
    }
    case PROPOSAL_TYPE.UPDATE_LISTING: {
      const images = extractGroupedImageUrls({ proposalData })
      const video = extractVideo({ proposalData })
      const specs = extractSpecs({ proposalData })
      const { copy, warranty } = extractCopyAndWarranty({ proposalData })
      const { attributeDisplayMode, attributes } = extractAttribute({ proposalData, defaultAttributesNames })
      const name = _.get(proposalData, PATHS.PRODUCT.NAME, false)
      const featureTitle = _.get(proposalData, PATHS.LISTING.FEATURE_TITLE, false)
      const shortDescription = _.get(proposalData, PATHS.PRODUCT.SHORT_DESCRIPTION, false)

      newProposal = {
        listing: {
          ...newProposal.listing,
          ...(featureTitle ? { featureTitle } : {})
        },
        product: {
          ...newProposal.product,
          ...(name ? { name } : {}),
          ...(shortDescription ? { shortDescription } : {}),
          // UPDATE_LISTING video 有三種情況 (刪除影片=undefined) (有影片=["url"]) (不做影片修改=[])
          ...(video === undefined ? { videos: [] } : (video.length > 0 ? { videos: video } : {})),
          images,
          specs,
          ...(copy ? { copy } : {}),
          ...(warranty
            ? {
              warranty: {
                ...warranty,
                scope: warranty.custom_scope || warranty.scope
              }
            }
            : {}),
          ...(attributeDisplayMode ? { attributeDisplayMode } : {}),
          ...(attributes ? { attributes } : {})
        }
      }

      break
    }
    case PROPOSAL_TYPE.UPDATE_LISTING_MODEL: {
      const specs = extractSpecs({ proposalData })
      const images = extractUpdateListingModelImageUrls({ proposalData, originModels })
      // 提案中無法從取得 attributes 使用 newProposal data 組成一個
      proposalData[PATHS.PRODUCT.ATTRIBUTES] = newProposal.product.attributes
      proposalData[PATHS.PRODUCT.ATTRIBUTE_DISPLAY_MODE] = newProposal.product.attributeDisplayMode
      const { attributeDisplayMode, attributes } = extractAttribute({ proposalData, defaultAttributesNames, specs })
      newProposal.product = {
        ...newProposal.product,
        specs,
        ...(attributeDisplayMode ? { attributeDisplayMode } : {}),
        ...(attributes ? { attributes } : {}),
        ...(images.length > 0 ? { images } : {})
      }
      break
    }
    case PROPOSAL_TYPE.UPDATE_VIDEO: {
      const video = extractVideo({ proposalData })
      // UPDATE_VIDEO 有兩種情況 (刪除影片=undefined) (有影片=["url"])
      newProposal.product.videos = video || []
      break
    }
    default:
      break
  }
  return newProposal
}

export const useProposal = (props, queryParams) => {
  const {
    actions: {
      fetchListings,
      fetchListingModelProposals,
      fetchListingInfoProposal,
      fetchProposals,
      fetchStruDataAttrClusterOptions,
      fetchCategoryOptions,
      fetchProducts
    },
    location,
    i13nMethods: { sendI13nPageViewByRoute }
  } = props
  const [isLoading, setIsLoading] = useState(false)
  const [proposal, setProposal] = useState(defaultProposal)

  // 用於獲取 Listing
  const fetchListingData = async (proposalData) => {
    const listingId = _.get(proposalData, PATHS.OTHER.PROPOSAL_ID, EMPTY_VALUE)
    const listings = await fetchListings({ id: listingId, fields: '' })
    return _.get(listings, PATHS.OTHER.LISTINGS_PAYLOAD, {})
  }

  // 用於獲取 Products
  const fetchProductsData = async (listingData) => {
    const sku = _.get(listingData, PATHS.OTHER.SKU, EMPTY_VALUE)
    const products = await fetchProducts({ id: sku, fields: '' })
    return _.get(products, PATHS.OTHER.PRODUCTS_PAYLOAD, {})
  }
  // 用於獲取 Proposal
  const fetchProposal = async () => {
    const proposalId = _.get(queryParams, PATHS.OTHER.PROPOSAL_ID, EMPTY_VALUE)
    const proposalType = _.get(queryParams, PATHS.OTHER.PROPOSAL_TYPE, EMPTY_VALUE)
    // 透過網址列的 type 決定使用哪個 fatchProposal
    const [proposalError, proposal] = await safeAwait(getProposalById({ fetchProposals, fetchListingInfoProposal, fetchListingModelProposals }, proposalId, proposalType))
    if (proposalError) {
      console.error('Error fetching proposal data:', proposalError)
      Toast.error(proposalError.message)
      return
    }

    // 只有 UPDATE_LISTING_MODEL 在外層，統一移動到 listing 內
    if (proposalType === PROPOSAL_TYPE.UPDATE_LISTING_MODEL) {
      proposal.listing.models = proposal.models
    }

    return _.get(proposal, PATHS.OTHER.PROPOSAL_LISTING, {})
  }

  const handleAsyncOperationCompletion = (error, errorMessage) => {
    setIsLoading(false)
    console.error(errorMessage, error)
    Toast.error(error.message)
  }

  const fetchProposalData = async () => {
    setIsLoading(true)
    const [proposalError, proposalData] = await safeAwait(fetchProposal())
    if (proposalError) {
      handleAsyncOperationCompletion(proposalError, 'Error fetching proposal data:')
      return
    }

    const [listingError, listingData] = await safeAwait(fetchListingData(proposalData))
    if (listingError) {
      handleAsyncOperationCompletion(listingError, 'Error fetching listing data:')
      return
    }

    // 需要從 productsData 拿到 gameContents
    const [[productsError, productsData], [defaultAttributesError, defaultAttributesNames]] = await Promise.all([
      safeAwait(fetchProductsData(listingData)),
      safeAwait(fetchDefaultAttributes({ listingData, fetchStruDataAttrClusterOptions, fetchCategoryOptions }))
    ])

    if (defaultAttributesError) {
      handleAsyncOperationCompletion(defaultAttributesError, 'Error fetching attributes data:')
      return
    }
    if (productsError) {
      handleAsyncOperationCompletion(productsError, 'Error fetching products data:')
      return
    }

    // 預設全部 sanitizedProposal 從 listing 組出來
    const listingProposal = constructSanitizedProposal({ listingData, productsData, defaultAttributesNames })

    // 使用 proposalData 的資料依照 proposalType 重組取代 listingProposal，利用 defaultAttributesNames 判斷 CustomAttrs
    // 需要多傳入原始的 listingData 的 models 因為在 updateListingModel 取得 Proposal 時既有的商品不會給圖片 url，需要再重組
    const proposal = updateProposalDataByType({ proposalData, listingProposal, defaultAttributesNames, queryParams, originModels: listingData.models })

    setProposal(proposal)
    setIsLoading(false)

    const currentRouteData = retrieveRouteByLocation({ location })
    // get prarams from url `proposalType`
    const proposalType = _.get(queryParams, PATHS.OTHER.PROPOSAL_TYPE, EMPTY_VALUE)
    sendI13nPageViewByRoute({ currentRouteData, pageKey: `${proposalType}-preview` }, updateListingProposalI13nConf)
  }

  useOnInit(fetchProposalData)
  return { proposal, isLoading }
}

const UpdateListingPreview = props => {
  const queryParams = typeof window !== 'undefined' ? HistoryUtil.retrieveQueryParams({ location: window.location }) : {}
  const { supplier, page, actions, isHideNavigation = true } = props
  const { proposal, isLoading } = useProposal(props, queryParams)
  return (
    <div>
      <Loader loaded={!isLoading}>
        <SilentPageProvider defaultValue>
          <Header
            key='header'
            supplier={supplier}
            page={page}
            actions={actions}
            isHideNavigation={isHideNavigation}
          />
        </SilentPageProvider>
        <ListingPreviewBody
          key='body'
          proposal={proposal}
        />
      </Loader>
    </div>
  )
}

export default withI13nWrapper(UpdateListingPreview)
